import { ChatVersion } from 'app/copied/constants/common';

import { logInfo } from './logger';

export enum StorageMode {
  InMemory = 'inMemory',
  Persistent = 'persistent',
}

export enum ModuleType {
  Webchat = 'webchat',
  CTA = 'cta',
  Adstra = 'adstra',
  Throtle = 'throtle',
  Signal = 'signal',
}

export interface IModuleConfig {
  module: string;
  module_type: ModuleType;
}

export interface IWebChatModule extends IModuleConfig {
  condition_uuid: string;
  enabled: boolean;
  localization_title: string;
  language_codes: string[];
  web_chat_version: ChatVersion;
  exclude_on_urls: string[];
}

export type ModuleConfig = IWebChatModule; // | IAdstraModule | IThrotleModule | ICtaModule; // introduce other of the modules if needed

export type EventCaptureHandler = (eventName: string, eventParams?: object) => void;

export interface IAnalyticsModule {
  addPersistentParams: (params: object) => void;
  captureEvent: EventCaptureHandler;
}

interface IModule {
  module: string;
  module_type: ModuleType;
}

export interface ISignalModule extends IModule, EventTarget {}

export interface ModuleManager {
  modules: IModule[];
}

export interface OstroManager {
  analytics: IAnalyticsModule;
  conditionId: string;
  featureFlags: object;
  ostroId: string;
  sessionId: string;
  rxId: string;
  webchats: IWebChatModule[];
  config: {
    modules: Map<ModuleType, ModuleConfig>;
  };
  moduleManager: ModuleManager;
}

export interface IOstroAdapter {
  getStorageMode(): StorageMode;
  getRxId(): string;
  getSessionId(): string;
  getConditionId(): string;
  getWebchatModules(): IWebChatModule[];
  getFeatureFlags(): object;
}

/**
 * This class is meant to be used only when running in localhost, as ostro.js is not run and thus no ID handler is present.
 * When running in higher environments, `window.ostro` will be properly defined because ostro.js will run first.
 */
class LocalBoostrapOstroManager implements OstroManager {
  private _conditionId = '';
  private _rxId = '';
  private _featureFlags: object = {};
  ostroId = '';
  sessionId = '';
  webchats: IWebChatModule[] = [];
  exclude_on_urls: string[] = [];
  moduleManager = { modules: [] };
  config = {
    modules: new Map<ModuleType, ModuleConfig>(),
  };
  analytics = {
    addPersistentParams: (params: object) => {
      // eslint-disable-next-line no-console
      logInfo('addPersistentParams', params, JSON.stringify(params));
    },
    captureEvent: (eventName: string, eventParams?: object) => {
      logInfo('captureEvent', eventName, eventParams, JSON.stringify(eventParams));
    },
  };

  get conditionId(): string {
    return this._conditionId || localStorage.getItem('rx_condition_id') || '';
  }

  set conditionId(newConditionId: string) {
    newConditionId ??= '';
    if (newConditionId !== this._conditionId) {
      // Move the rx ID to the new key
      localStorage.removeItem(this._conditionId);
      if (this._rxId) {
        localStorage.setItem(newConditionId, this._rxId);
      }

      this._conditionId = newConditionId;
      localStorage.setItem('rx_condition_id', newConditionId);
    }
  }

  get rxId(): string {
    return this._rxId;
  }

  set rxId(newRxId: string) {
    newRxId ??= '';
    if (newRxId !== this._rxId) {
      this._rxId = newRxId;
      localStorage.setItem(this._conditionId, newRxId);
    }
  }

  get featureFlags(): object {
    return this._featureFlags;
  }

  set featureFlags(newFeatureFlags: object) {
    newFeatureFlags ??= {};
    const stringifiedNewFeatureFlags = JSON.stringify(newFeatureFlags);
    if (JSON.stringify(this._featureFlags) !== stringifiedNewFeatureFlags) {
      this._featureFlags = newFeatureFlags;
      localStorage.setItem('rx_feature_flags', stringifiedNewFeatureFlags);
    }
  }
}

class OstroAdapter implements IOstroAdapter {
  ostro: OstroManager;

  constructor() {
    this.ostro = window.ostro || new LocalBoostrapOstroManager();
  }

  getStorageMode(): StorageMode {
    // For now, we only support persistent and in-memory storage modes based on the session id
    return this.getRxId() ? StorageMode.Persistent : StorageMode.InMemory;
  }

  getRxId(): string {
    return this.ostro.rxId || '';
  }

  getSessionId(): string {
    return this.ostro.sessionId || '';
  }

  getConditionId(): string {
    return this.ostro.conditionId || '';
  }

  getWebchatModules(): IWebChatModule[] {
    return this.ostro.webchats || [];
  }

  getFeatureFlags(): object {
    return this.ostro.featureFlags || {};
  }
}

export const ostroAdapter = new OstroAdapter();
